import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuthState } from "react-firebase-hooks/auth";
import {
  Copy,
  Download,
  Youtube,
  MessageSquare,
  Users,
  Target,
  Zap,
  Sparkles,
  Clock,
  BarChart2,
  Lightbulb,
  ThumbsUp,
  MessageCircle,
  Share2,
} from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import auth from "../../../../firebase.init";
import { deductUserCredit } from "../../../../utils/geminiUtils";

const YoutubeCommunityPostGenerator = ({
  toolPath = "/user/community-post-generator",
}) => {
  // --- AUTH & CORE STATE ---
  const [user, loadingAuth] = useAuthState(auth);
  const [loading, setLoading] = useState(true);
  const [loadingKeys, setLoadingKeys] = useState(true);
  const [loadingTools, setLoadingTools] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  // --- FORM STATE ---
  const [topic, setTopic] = useState("");
  const [audience, setAudience] = useState("General Audience");
  const [postType, setPostType] = useState("Engagement Booster");
  const [tone, setTone] = useState("Curious");

  // Toggles
  const [includeCta, setIncludeCta] = useState(true);
  const [includeEmojis, setIncludeEmojis] = useState(true);
  const [multipleVariations, setMultipleVariations] = useState(true);

  // --- OUTPUT STATE ---
  const [generatedPosts, setGeneratedPosts] = useState(null);

  // --- CREDIT & TOOL CONFIG ---

  const [creditCost, setCreditCost] = useState(2);
  const [apiKeys, setApiKeys] = useState([]);

  // --- OPTIONS ---
  const audienceOptions = [
    "General Audience",
    "Current Subscribers",
    "New/Potential Viewers",
    "Gamers",
    "Tech Enthusiasts",
    "Entrepreneurs & Founders",
    "Students & Learners",
    "Creatives & Artists",
    "Fitness & Health Enthusiasts",
    "Vlog Followers",
  ];

  const postTypeOptions = [
    "Question",
    "Poll",
    "Teaser",
    "Announcement",
    "Educational Tip",
    "Behind the Scenes",
    "Engagement Booster",
  ];

  const toneOptions = [
    "Casual",
    "Energetic",
    "Curious",
    "Professional",
    "Dramatic",
  ];

  // Fetch API keys
  const fetchApiKeys = async () => {
    try {
      const response = await axios.get(`https://allinoneai-d1a5a1b54c64.herokuapp.com/api/gemini-keys`);
      if (response.data && response.data.success && response.data.data) {
        const keys = response.data.data.map((keyObj) => keyObj.key);
        setApiKeys(keys);
        if (keys.length === 0)
          setError("No API keys available. Please contact administrator.");
      } else {
        setError("Failed to load API keys");
      }
    } catch (err) {
      console.error("Error fetching API keys:", err);
      setError("Error loading API keys");
    } finally {
      setLoadingKeys(false);
    }
  };

  // Fetch Tools List
  const fetchToolsList = async () => {
    try {
      const response = await axios.get(
        `https://allinoneai-d1a5a1b54c64.herokuapp.com/api/tools-section`,
      );
      if (
        response.data &&
        response.data.success &&
        response.data.data &&
        response.data.data.tools
      ) {
        const tool = response.data.data.tools.find((t) => t.id === 8);
        if (tool) {
          setCreditCost(tool.creditCost);
        }
      }
    } catch (err) {
      console.warn("Could not fetch tools list, using default credit cost");
    } finally {
      setLoadingTools(false);
    }
  };

  const getRandomApiKey = () => {
    if (apiKeys.length === 0) throw new Error("No API keys available");
    return apiKeys[Math.floor(Math.random() * apiKeys.length)];
  };

  // Initial Data Fetch
  useEffect(() => {
    const fetchData = async () => {
      if (loadingAuth) return;
      if (!user) {
        setError("User not logged in");
        setLoading(false);
        setLoadingKeys(false);
        setLoadingTools(false);
        return;
      }
      try {
        await Promise.all([fetchApiKeys(), fetchToolsList()]);

        const userResponse = await axios.get(`https://allinoneai-d1a5a1b54c64.herokuapp.com/users`, {
          params: { userEmail: user.email },
        });
        if (userResponse.data && userResponse.data.length > 0) {
          const userData = userResponse.data[0];
        } else {
          setError("User data not found");
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, loadingAuth]);

  // Prompt Engineering
  const constructPrompt = () => {
    return `You are an expert YouTube Community Manager and engagement strategist.
    Create high-converting, scroll-stopping YouTube Community Posts based on these parameters:
    
    - Topic: ${topic}
    - Target Audience: ${audience}
    - Post Type: ${postType}
    - Tone: ${tone}
    - Include Call to Action (CTA): ${includeCta ? "Yes" : "No"}
    - Include Emojis: ${includeEmojis ? "Yes (use naturally, don't overdo it)" : "No (STRICTLY NO EMOJIS)"}
    - Generate Multiple Variations: ${multipleVariations ? "Yes, generate exactly 3 distinctly different post variations" : "No, generate exactly 1 optimized post"}
    
    RULES:
    1. Posts must be concise and natively optimized for the YouTube Community tab format.
    2. Focus heavily on driving likes, comments, and votes.
    3. ${postType === "Poll" ? "Since Post Type is 'Poll', YOU MUST provide 3 to 4 highly engaging poll options." : "Do NOT provide poll options unless it naturally fits."}
    
    CRITICAL INSTRUCTION:
    Respond ONLY with a raw, valid JSON array of objects. Do not wrap in markdown \`\`\`json blocks.
    
    JSON SCHEMA REQUIRED:
    [
      {
        "hookLine": "The attention-grabbing first sentence.",
        "mainPostText": "The core message of the post (1-4 short lines).",
        "engagementTrigger": "The question or debate starter to drive comments.",
        "suggestedCta": "${includeCta ? "The specific Call to Action (e.g., watch the new video, click the link)" : ""}",
        "pollOptions": ${postType === "Poll" ? '["Option 1", "Option 2", "Option 3"]' : "[]"},
        "postingStrategy": "1 brief sentence on the best time or intent for posting this."
      }
    ]`;
  };

  const generatePosts = async () => {
    if (!topic.trim()) {
      setError("Please provide a Topic.");
      return;
    }
    if (apiKeys.length === 0) {
      setError("No API keys available. Please try again later.");
      return;
    }

    setGenerating(true);
    setError("");
    setGeneratedPosts(null);

    try {
      const MODEL_ID = "gemini-2.5-flash-lite";
      const apiKey = getRandomApiKey();
      const API_URL = `https://generativelanguage.googleapis.com/v1beta/models/${MODEL_ID}:generateContent?key=${apiKey}`;
      const prompt = constructPrompt();

      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contents: [{ role: "user", parts: [{ text: prompt }] }],
          generationConfig: {
            temperature: 0.85,
            topK: 40,
            topP: 0.95,
            maxOutputTokens: 2500,
          },
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();

      if (
        data.candidates &&
        data.candidates.length > 0 &&
        data.candidates[0].content
      ) {
        let generatedText = data.candidates[0].content.parts[0].text;

        generatedText = generatedText
          .replace(/```json/g, "")
          .replace(/```/g, "")
          .trim();

        try {
          const parsedContent = JSON.parse(generatedText);
          setGeneratedPosts(
            Array.isArray(parsedContent) ? parsedContent : [parsedContent],
          );
          toast.success("Community Posts generated!");
        } catch (parseErr) {
          throw new Error("AI returned invalid data format. Please try again.");
        }
      } else {
        throw new Error("No content was generated from the API response");
      }
    } catch (err) {
      console.error("Error generating content:", err);
      setError(`Error: ${err.message}`);
      toast.error(`Error: ${err.message}`);

      try {
      } catch (refundErr) {
        console.error("Error refunding credits:", refundErr);
      }
    } finally {
      setGenerating(false);
    }
  };

  const getFullPostText = (post) => {
    let text = `${post.hookLine}\n\n${post.mainPostText}\n\n${post.engagementTrigger}`;
    if (post.suggestedCta) text += `\n\n${post.suggestedCta}`;
    if (post.pollOptions && post.pollOptions.length > 0) {
      text += `\n\n📊 Poll Options:\n`;
      post.pollOptions.forEach((opt) => (text += `- ${opt}\n`));
    }
    return text;
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        toast.success(`${label} copied!`);
      })
      .catch(() => {
        toast.error(`Failed to copy ${label}`);
      });
  };

  const downloadContent = () => {
    if (!generatedPosts) return;

    let text = `YOUTUBE COMMUNITY POSTS\nTopic: ${topic}\n\n`;

    generatedPosts.forEach((post, index) => {
      text += `=== VARIATION ${index + 1} ===\n`;
      text += getFullPostText(post);
      text += `\n\n💡 Strategy: ${post.postingStrategy}\n`;
      text += `\n--------------------------------------------------\n\n`;
    });

    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${topic.replace(/\s+/g, "-")}-community-posts.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Posts downloaded!");
  };

  if (loadingAuth || loading || loadingKeys || loadingTools) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          background: "#f8fafc",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              border: "4px solid #f1f5f9",
              borderTop: "4px solid #f43f5e",
              borderRadius: "50%",
              width: "40px",
              height: "40px",
              animation: "spin 1s linear infinite",
              margin: "0 auto 10px",
            }}
          ></div>
          <p style={{ color: "#64748b", fontWeight: "500" }}>
            Loading Workspace...
          </p>
        </div>

        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!user) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          background: "#f8fafc",
          padding: "1rem",
        }}
      >
        <div
          style={{
            background: "white",
            padding: "3rem 2rem",
            borderRadius: "16px",
            boxShadow: "0 10px 40px rgba(0,0,0,0.08)",
            textAlign: "center",
            maxWidth: "400px",
            width: "100%",
          }}
        >
          <MessageSquare
            size={48}
            color="#f43f5e"
            style={{ marginBottom: "1rem" }}
          />
          <h2
            style={{
              color: "#0f172a",
              marginBottom: "1.5rem",
              fontSize: "1.5rem",
              fontWeight: "700",
            }}
          >
            Authentication Required
          </h2>
          <button
            onClick={() => (window.location.href = "/login")}
            style={{
              padding: "12px 24px",
              background: "linear-gradient(135deg, #f43f5e 0%, #e11d48 100%)",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              fontWeight: "600",
              width: "100%",
            }}
          >
            Proceed to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .post-app { font-family: 'Inter', system-ui, sans-serif; background: #f8fafc; min-height: 100vh; color: #334155; padding-bottom: 4rem; }
        .app-container { max-width: 1400px; margin: 0 auto; padding: 1.5rem 2rem; }
        
        /* Top Bar */
        .top-bar { display: flex; justify-content: space-between; align-items: center; margin-bottom: 2rem; }
        .back-btn { background: #fff; border: 1px solid #e2e8f0; padding: 8px 16px; border-radius: 8px; cursor: pointer; display: flex; align-items: center; gap: 8px; font-weight: 600; color: #64748b; transition: all 0.2s; }
        .back-btn:hover { background: #f1f5f9; color: #0f172a; }
        .credits-badge { display: flex; align-items: center; gap: 12px; background: white; padding: 8px 20px; border-radius: 30px; border: 1px solid #e2e8f0; box-shadow: 0 2px 10px rgba(0,0,0,0.02); }
        .refill-btn { background: #10b981; color: white; border: none; padding: 6px 14px; border-radius: 20px; cursor: pointer; font-size: 0.8rem; font-weight: 700; transition: 0.2s; }
        .refill-btn:hover { background: #059669; }

        /* Header Area */
        .header-area { text-align: center; margin-bottom: 3.5rem; }
        .page-title { font-size: 2.5rem; font-weight: 800; color: #0f172a; display: flex; align-items: center; justify-content: center; gap: 12px; letter-spacing: -0.02em; margin: 0; }
        .page-subtitle { color: #64748b; font-size: 1.1rem; max-width: 650px; margin: 12px auto 0; line-height: 1.6; }

        /* Layout Grid */
        /* Changed align-items to flex-start so columns don't stretch */
        .main-grid { display: flex; flex-wrap: wrap; gap: 2rem; align-items: flex-start; }
        /* Added position sticky to sidebar so it stays on screen when scrolling results */
        .sidebar { flex: 1 1 350px; max-width: 450px; position: sticky; top: 2rem; }
        .content-area { flex: 1 1 500px; }

        /* Form Components */
        .saas-card { background: #ffffff; border-radius: 16px; box-shadow: 0 4px 24px rgba(0,0,0,0.04); border: 1px solid #e2e8f0; padding: 1.75rem; display: flex; flex-direction: column; }
        .form-label { font-weight: 600; color: #475569; font-size: 0.9rem; margin-bottom: 0.5rem; display: block; }
        .form-control, .form-select { width: 100%; padding: 0.85rem 1rem; border-radius: 10px; border: 1px solid #cbd5e1; background: #f8fafc; font-size: 0.95rem; color: #0f172a; transition: all 0.2s; margin-bottom: 1.25rem; box-sizing: border-box; }
        .form-control:focus, .form-select:focus { outline: none; border-color: #f43f5e; box-shadow: 0 0 0 3px rgba(244, 63, 94, 0.1); background: #fff; }
        .form-row { display: flex; gap: 1rem; }
        .form-col { flex: 1; }

        /* Changed margin-top: auto to margin-top: 1rem so button doesn't float away */
        .gradient-btn { background: linear-gradient(135deg, #f43f5e 0%, #be123c 100%); color: white; border: none; padding: 1rem 1.5rem; border-radius: 10px; font-weight: 600; font-size: 1rem; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 0.5rem; transition: all 0.3s ease; width: 100%; box-shadow: 0 4px 12px rgba(244, 63, 94, 0.25); margin-top: 1rem; }
        .gradient-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 6px 16px rgba(244, 63, 94, 0.35); }
        .gradient-btn:disabled { background: #cbd5e1; box-shadow: none; cursor: not-allowed; transform: none; }
        
        /* Toggles */
        .toggles-container { background: #f8fafc; padding: 1.25rem; border-radius: 12px; margin-top: 0.5rem; margin-bottom: 1rem; border: 1px solid #e2e8f0; display: flex; flex-direction: column; gap: 1rem; }
        .toggle-row { display: flex; justify-content: space-between; align-items: center; gap: 1rem; }
        .toggle-text { flex: 1; }
        .toggle-title { font-weight: 600; color: #1e293b; display: block; font-size: 0.9rem; }
        .toggle-desc { font-size: 0.75rem; color: #64748b; }
        .toggle-switch { position: relative; display: inline-block; width: 44px; height: 24px; flex-shrink: 0; }
        .toggle-switch input { opacity: 0; width: 0; height: 0; }
        .slider { position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; background-color: #cbd5e1; transition: .4s; border-radius: 34px; }
        .slider:before { position: absolute; content: ""; height: 18px; width: 18px; left: 3px; bottom: 3px; background-color: white; transition: .4s; border-radius: 50%; }
        input:checked + .slider { background-color: #10b981; }
        input:checked + .slider:before { transform: translateX(20px); }

        /* Results Area */
        .results-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.5rem; flex-wrap: wrap; gap: 1rem; }
        .results-title { margin: 0; font-size: 1.5rem; color: #0f172a; font-weight: 700; }
        .export-btn { background: #10b981; color: #fff; border: none; padding: 8px 16px; border-radius: 8px; cursor: pointer; display: flex; align-items: center; gap: 6px; font-size: 0.9rem; font-weight: 600; box-shadow: 0 2px 4px rgba(16, 185, 129, 0.2); transition: 0.2s; }
        .export-btn:hover { background: #059669; }

        .post-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; margin-bottom: 2rem; overflow: hidden; box-shadow: 0 4px 12px rgba(0,0,0,0.03); }
        .post-header { background: #f8fafc; padding: 1rem 1.5rem; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
        .post-number { background: #f43f5e; color: white; width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 0.8rem; }
        .copy-btn { background: white; border: 1px solid #cbd5e1; color: #64748b; padding: 6px 12px; border-radius: 6px; font-size: 0.8rem; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 4px; transition: 0.2s; }
        .copy-btn:hover { background: #f1f5f9; color: #0f172a; }
        
        /* YouTube Native Style Mockup */
        .yt-mockup { padding: 1.5rem; font-family: 'Roboto', sans-serif; }
        .yt-text { font-size: 0.95rem; color: #0f172a; line-height: 1.6; white-space: pre-wrap; margin-bottom: 1rem; word-break: break-word; }
        .yt-poll { border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; margin-top: 1rem; }
        .yt-poll-option { padding: 12px 16px; border-bottom: 1px solid #f1f5f9; background: #f8fafc; display: flex; justify-content: space-between; align-items: center; color: #334155; font-weight: 500; font-size: 0.9rem; word-break: break-word; }
        .yt-poll-option:last-child { border-bottom: none; }
        .yt-actions { display: flex; gap: 24px; color: #64748b; margin-top: 1.5rem; padding-top: 1rem; border-top: 1px solid #f1f5f9; }
        .yt-action-item { display: flex; align-items: center; gap: 6px; font-size: 0.85rem; font-weight: 500;}
        
        .strategy-box { background: #fffbeb; border: 1px solid #fde68a; padding: 12px 16px; border-radius: 8px; margin: 0 1.5rem 1.5rem 1.5rem; color: #92400e; font-size: 0.9rem; display: flex; align-items: flex-start; gap: 10px; }
        
        @keyframes custom-pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.8; transform: scale(0.98); } }
        .anim-pulse { animation: custom-pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite; }

        /* Responsive Design */
        @media (max-width: 992px) {
          .sidebar { max-width: 100%; flex: 1 1 100%; position: relative; top: 0; }
          .content-area { flex: 1 1 100%; }
        }

        @media (max-width: 768px) {
          .app-container { padding: 1rem; }
          .top-bar { flex-direction: column; gap: 1rem; align-items: stretch; margin-bottom: 1.5rem; }
          .back-btn, .credits-badge { justify-content: center; width: 100%; box-sizing: border-box; }
          
          .header-area { margin-bottom: 2rem; }
          .page-title { font-size: 1.8rem; flex-wrap: wrap; text-align: center; }
          .page-subtitle { font-size: 1rem; padding: 0 0.5rem; }
          
          .form-row { flex-direction: column; gap: 0; }
          .saas-card { padding: 1.25rem; }
          
          .results-header { flex-direction: column; align-items: stretch; }
          .export-btn { justify-content: center; width: 100%; }
          
          .post-header { flex-direction: column; align-items: flex-start; gap: 1rem; }
          .copy-btn { width: 100%; justify-content: center; }
          
          .yt-mockup { padding: 1rem; }
          .yt-actions { gap: 12px; justify-content: space-between; }
          .strategy-box { flex-direction: column; margin: 0 1rem 1rem 1rem; }
        }
      `}</style>

      <div className="post-app">
        <div className="app-container">
          {/* Top Navigation */}
          <div className="top-bar">
            <button className="back-btn" onClick={() => window.history.back()}>
              ← Dashboard
            </button>
          </div>

          {/* Header */}
          <div className="header-area">
            <h1 className="page-title">
              <MessageSquare color="#f43f5e" size={40} /> Community Post
              Generator
            </h1>
            <p className="page-subtitle">
              Generate engaging, interactive posts and polls to boost your
              channel's algorithm signals and audience retention.
            </p>
          </div>

          {error && (
            <div
              style={{
                background: "#fef2f2",
                color: "#ef4444",
                padding: "1rem",
                borderRadius: "10px",
                marginBottom: "2rem",
                textAlign: "center",
                border: "1px solid #fecaca",
                fontWeight: "600",
              }}
            >
              {error}
            </div>
          )}

          <div className="main-grid">
            {/* Left Column - Form */}
            <div className="sidebar">
              <div className="saas-card">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "1.5rem",
                    paddingBottom: "1rem",
                    borderBottom: "1px solid #f1f5f9",
                  }}
                >
                  <Target size={20} color="#f43f5e" />
                  <h3
                    style={{
                      margin: 0,
                      fontSize: "1.25rem",
                      color: "#0f172a",
                      fontWeight: "700",
                    }}
                  >
                    Post Strategy
                  </h3>
                </div>

                <label className="form-label">
                  Core Topic / Subject{" "}
                  <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <textarea
                  className="form-control"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="e.g., Sneak peek of the new studio setup, Thoughts on AI updates..."
                  rows={2}
                />

                <label className="form-label">Target Audience</label>
                <select
                  className="form-select"
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                >
                  {audienceOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>

                <div className="form-row">
                  <div className="form-col">
                    <label className="form-label">Post Type</label>
                    <select
                      className="form-select"
                      value={postType}
                      onChange={(e) => setPostType(e.target.value)}
                    >
                      {postTypeOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-col">
                    <label className="form-label">Vibe / Tone</label>
                    <select
                      className="form-select"
                      value={tone}
                      onChange={(e) => setTone(e.target.value)}
                    >
                      {toneOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="toggles-container">
                  <div className="toggle-row">
                    <div className="toggle-text">
                      <span className="toggle-title">Include Emojis</span>
                      <span className="toggle-desc">
                        Make it visually expressive
                      </span>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={includeEmojis}
                        onChange={(e) => setIncludeEmojis(e.target.checked)}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>

                  <div className="toggle-row">
                    <div className="toggle-text">
                      <span className="toggle-title">
                        Include Call to Action
                      </span>
                      <span className="toggle-desc">
                        Drive traffic to links/videos
                      </span>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={includeCta}
                        onChange={(e) => setIncludeCta(e.target.checked)}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>

                  <div
                    className="toggle-row"
                    style={{
                      borderTop: "1px solid #e2e8f0",
                      paddingTop: "1rem",
                      marginTop: "0.25rem",
                    }}
                  >
                    <div className="toggle-text">
                      <span className="toggle-title">Multiple Variations</span>
                      <span className="toggle-desc">
                        Generate 3 unique posts
                      </span>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={multipleVariations}
                        onChange={(e) =>
                          setMultipleVariations(e.target.checked)
                        }
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                </div>

                <button
                  onClick={generatePosts}
                  disabled={generating || !topic.trim()}
                  className="gradient-btn"
                >
                  {generating ? (
                    <Clock size={18} className="fa-spin" />
                  ) : (
                    <Sparkles size={18} />
                  )}
                  {generating ? "Crafting Posts..." : `Generate Posts `}
                </button>
              </div>
            </div>

            {/* Right Column - Results */}
            <div className="content-area">
              <div className="results-header">
                <h2 className="results-title">Generated Posts</h2>
                {generatedPosts && (
                  <button className="export-btn" onClick={downloadContent}>
                    <Download size={16} /> Export Posts
                  </button>
                )}
              </div>

              {generating ? (
                <div
                  className="saas-card anim-pulse"
                  style={{
                    textAlign: "center",
                    padding: "6rem 2rem",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <div
                    style={{
                      width: "70px",
                      height: "70px",
                      background: "#ffe4e6",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 1.5rem",
                    }}
                  >
                    <Users size={34} color="#f43f5e" />
                  </div>
                  <h3
                    style={{
                      color: "#0f172a",
                      fontWeight: "800",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Optimizing for Engagement...
                  </h3>
                  <p
                    style={{
                      color: "#64748b",
                      maxWidth: "350px",
                      margin: "0 auto",
                    }}
                  >
                    Crafting hooks, structuring polls, and designing
                    algorithm-friendly community interactions.
                  </p>
                </div>
              ) : generatedPosts ? (
                <div>
                  {generatedPosts.map((post, index) => (
                    <div key={index} className="post-card">
                      {/* Card Header */}
                      <div className="post-header">
                        <h3
                          style={{
                            margin: 0,
                            fontSize: "1.05rem",
                            fontWeight: "700",
                            color: "#1e293b",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <span className="post-number">{index + 1}</span>
                          Option {index + 1}
                        </h3>
                        <button
                          className="copy-btn"
                          onClick={() =>
                            copyToClipboard(getFullPostText(post), "Post")
                          }
                        >
                          <Copy size={14} /> Copy Text
                        </button>
                      </div>

                      {/* YouTube Native Style Mockup */}
                      <div className="yt-mockup">
                        <div className="yt-text">
                          <strong>{post.hookLine}</strong>
                          {"\n\n"}
                          {post.mainPostText}
                          {"\n\n"}
                          {post.engagementTrigger}
                          {post.suggestedCta && `\n\n${post.suggestedCta}`}
                        </div>

                        {post.pollOptions && post.pollOptions.length > 0 && (
                          <div className="yt-poll">
                            {post.pollOptions.map((opt, i) => (
                              <div key={i} className="yt-poll-option">
                                <span>{opt}</span>
                                <span style={{ color: "#94a3b8" }}>
                                  <BarChart2 size={16} />
                                </span>
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="yt-actions">
                          <div className="yt-action-item">
                            <ThumbsUp size={18} /> Like
                          </div>
                          <div className="yt-action-item">
                            <MessageCircle size={18} /> Comment
                          </div>
                          <div className="yt-action-item">
                            <Share2 size={18} /> Share
                          </div>
                        </div>
                      </div>

                      {/* Strategy Box */}
                      <div className="strategy-box">
                        <Lightbulb
                          size={20}
                          color="#d97706"
                          style={{ flexShrink: 0, marginTop: "2px" }}
                        />
                        <div>
                          <strong>Strategy Tip:</strong> {post.postingStrategy}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div
                  className="saas-card"
                  style={{
                    textAlign: "center",
                    padding: "6rem 2rem",
                    border: "2px dashed #cbd5e1",
                    background: "transparent",
                    boxShadow: "none",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <MessageSquare
                    size={56}
                    color="#cbd5e1"
                    style={{ marginBottom: "1.5rem" }}
                  />
                  <h3
                    style={{
                      color: "#64748b",
                      fontWeight: "700",
                      margin: "0 0 0.5rem 0",
                    }}
                  >
                    Ready to Engage
                  </h3>
                  <p style={{ color: "#94a3b8", margin: 0, maxWidth: "350px" }}>
                    Enter your topic on the left to generate posts tailored to
                    spike interaction on your Community tab.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default YoutubeCommunityPostGenerator;
